import { useDialogStore } from '@ariakit/react'
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors
} from '@dnd-kit/core'
import {
	arrayMove,
	rectSortingStrategy,
	SortableContext
} from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	GridLayout01Icon,
	GridLayout02Icon,
	GridLayout03Icon,
	GridLayout04Icon,
	MoveIcon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip } from '@/components/ui/tooltip'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import { useExperienceVettingPhotoUpload } from '@/features/service-vetting/store/services-photo-upload'
import { ProPhotosDeleteImageModal } from '@/features/submission-pro-photos-upload/components/pro-photos-delete-image-modal'
import { ProPhotosEmptyGalleryState } from '@/features/submission-pro-photos-upload/components/pro-photos-empty-gallery-state'
import { ProPhotosGalleryItem } from '@/features/submission-pro-photos-upload/components/pro-photos-gallery-item'
import { useTranslation } from '@/translations/i18n'
import { useDeleteExperienceVettingPhotos } from '../queries/use-delete-experience-vetting-photos'
import { useUpdateExperiencVettingCoverPhoto } from '../queries/use-update-experienc-vetting-cover-photo'
import { useUpdateExperienceVettingGalleryImagesPositions } from '../queries/use-update-experience-vetting-gallery-images-positions'
import type { RemapExperienceVetting } from '../types'
import { ExpVettingCriteriaGalleryUploadingItem } from './exp-vetting-criteria-gallery-uploading-item'
import { ExperienceVettingCriteriaUploadButton } from './experience-vetting-criteria-upload-button'

export const MINIMUM_IMAGES_REQUIRED = 5
const GRID_LAYOUT_ICONS = [
	GridLayout01Icon,
	GridLayout02Icon,
	GridLayout03Icon,
	GridLayout04Icon
]

export const ExpProPhotosGrid = ({
	experience
}: {
	experience: RemapExperienceVetting
}) => {
	const { t } = useTranslation()
	const { gallery } = experience
	const [activeId, setActiveId] = useState<number | string | null>(null)
	const [orderedImages, setOrderedImages] = useState(gallery.images)
	const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
		null
	)
	const [showGalleryModal, setShowGalleryModal] = useState(false)
	const { galleryItems: uploadingItems } = useExperienceVettingPhotoUpload()
	const deleteImageStore = useDialogStore()
	const [imageToDelete, setImageToDelete] =
		useState<ExperienceGalleryImage | null>(null)

	const updateExperienceVettingGalleryImagesPositions =
		useUpdateExperienceVettingGalleryImagesPositions({
			experienceId: experience.id,
			galleryId: experience.gallery.id
		})
	const updateExperienceCoverPhotoMutation =
		useUpdateExperiencVettingCoverPhoto({
			experienceId: experience.id,
			galleryId: experience.gallery.id
		})

	const handleChangeCoverPhoto = (id: number) => {
		updateExperienceCoverPhotoMutation.mutate({
			isCover: true,
			imageId: id
		})
	}

	useEffect(() => {
		setOrderedImages(gallery.images)
	}, [gallery.images])

	const onCloseDeletePhotoModal = () => {
		setImageToDelete(null)
		deleteImageStore.toggle()
	}

	const deletePhotoMutation = useDeleteExperienceVettingPhotos(
		{ galleryId: experience.gallery.id, experienceId: experience.id },
		{ onSuccess: onCloseDeletePhotoModal }
	)

	const onClickDeletePhoto = (
		e: React.MouseEvent<HTMLButtonElement>,
		imageId: ExperienceGalleryImage['id']
	) => {
		e.stopPropagation()
		deletePhotoMutation.mutate({ imageIds: [imageId] })
	}

	const handleCloseModal = () => {
		setExpandedImageIndex(null)
		setShowGalleryModal(false)
	}

	const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id)
	}
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		if (!over) {
			setActiveId(null)
			return
		}

		const oldIndex = orderedImages.findIndex(
			(image) => image.id === active.id
		)
		const newIndex = orderedImages.findIndex(
			(image) => image.id === over.id
		)

		if (oldIndex !== newIndex) {
			// Update local state immediately for responsive UI
			const newOrderedImages = arrayMove(
				[...orderedImages],
				oldIndex,
				newIndex
			)
			setOrderedImages(newOrderedImages)

			updateExperienceVettingGalleryImagesPositions.mutate(
				newOrderedImages.map((image, index) => ({
					id: image.id,
					position: index + 1
				}))
			)
		}

		setActiveId(null)
	}
	const handleDragCancel = () => {
		setActiveId(null)
	}

	const handleClickExpandedImage = (
		imageId: ExperienceGalleryImage['id']
	) => {
		const index = orderedImages.findIndex((image) => image.id === imageId)
		setExpandedImageIndex(index)
		setShowGalleryModal(true)
	}

	useGalleryNeedsReviewShortcuts({
		selectableImages: orderedImages,
		currentDetailImageIndex: expandedImageIndex || 0,
		setCurrentDetailImageIndex: setExpandedImageIndex,
		onClose: handleCloseModal
	})

	return (
		<>
			{experience.gallery.images.length > 0 ? (
				<div className='grid w-full grid-cols-3 max-lg:grid-cols-2'>
					<DndContext
						collisionDetection={closestCenter}
						onDragCancel={handleDragCancel}
						onDragEnd={handleDragEnd}
						onDragStart={handleDragStart}
						sensors={sensors}
					>
						<SortableContext
							items={orderedImages}
							strategy={rectSortingStrategy}
						>
							{orderedImages.map((image, index) => {
								const GridLayoutIcon = GRID_LAYOUT_ICONS[index]
								return (
									<ProPhotosGalleryItem
										bottomLeftSlot={
											<div className='flex flex-wrap items-center gap-1'>
												{index < 4 && (
													<Tooltip
														content={
															<div>
																<p className='font-medium'>
																	{t(
																		'vetting:reordering.experience.placement_on_the_pdp_grid'
																	)}
																</p>
																<span className='font-normal text-neutral-300'>
																	{t(
																		'vetting:reordering.experience.drag_and_drop_to_change_placement'
																	)}
																</span>
															</div>
														}
														isOpenOnClickEnabled={
															false
														}
														maxWidth={'200px'}
													>
														<Chip
															className='[&_span]:underline [&_span]:decoration-[11%] [&_span]:decoration-neutral-400 [&_span]:decoration-dotted [&_span]:underline-offset-auto [&_svg]:size-4'
															endSlot={
																<GridLayoutIcon
																	height={16}
																	viewBox='0 0 16 16'
																	width={16}
																/>
															}
															label={t(
																'vetting:reordering.grid',
																{
																	number:
																		index +
																		1
																}
															)}
															shape='roundedRectangle'
															variant={'neutral'}
														/>
													</Tooltip>
												)}

												{image.isCover ? (
													<Chip
														label={t(
															'vetting:reordering.cover'
														)}
														shape='roundedRectangle'
														variant={'neutral'}
													/>
												) : (
													<Chip
														className='show-on-hover cursor-pointer [&_span]:underline [&_span]:decoration-[7.5%] [&_span]:decoration-neutral-400 [&_span]:decoration-solid [&_span]:underline-offset-auto'
														endSlot={
															updateExperienceCoverPhotoMutation.isPending && (
																<Spinner />
															)
														}
														label={t(
															'vetting:reordering.set_as_cover'
														)}
														onClick={() =>
															!updateExperienceCoverPhotoMutation.isPending &&
															handleChangeCoverPhoto(
																image.id
															)
														}
														shape='roundedRectangle'
														variant={'neutral'}
													/>
												)}
											</div>
										}
										imageId={image.id}
										imageName={image.name}
										imageUrl={
											image.thumbnails?.xs || image.url
										}
										isHostApprovalNeeded={
											image.isHostApprovalNeeded
										}
										isPendingImage={
											image.isPending ?? false
										}
										isProPhotography={
											image.isProPhotography ?? false
										}
										key={image.id}
										onClickExpand={() =>
											handleClickExpandedImage(image.id)
										}
										onDeleteModalOpen={() => {
											setImageToDelete(image)
											deleteImageStore.toggle()
										}}
									/>
								)
							})}
						</SortableContext>
						<DragOverlay style={{ transformOrigin: '0 0' }}>
							{activeId ? (
								<GalleryImageItem
									src={
										orderedImages.find(
											(image) => image.id === activeId
										)?.thumbnails?.xs ||
										orderedImages.find(
											(image) => image.id === activeId
										)?.url ||
										''
									}
									style={{
										cursor: 'grabbing'
									}}
									topLeftSlot={
										<ButtonLegacy
											className='show-on-hover'
											shape='square'
											size='sm'
											type='button'
											variant='secondary'
										>
											<MoveIcon size={16} />
										</ButtonLegacy>
									}
								/>
							) : null}
						</DragOverlay>
					</DndContext>

					{uploadingItems.map((image) => (
						<ExpVettingCriteriaGalleryUploadingItem
							hideAction={false}
							image={image}
							key={image.id}
						/>
					))}
				</div>
			) : (
				<ProPhotosEmptyGalleryState
					minimumImagesRequired={MINIMUM_IMAGES_REQUIRED}
					uploadButton={
						<ExperienceVettingCriteriaUploadButton
							buttonProps={{
								variant: 'tertiary',
								size: 'sm',
								type: 'button'
							}}
							buttonText='vetting:reordering.upload_from_device'
							experienceId={experience.id}
							galleryId={experience.gallery.id}
							withIcon={false}
						/>
					}
				/>
			)}

			{showGalleryModal && expandedImageIndex !== null && (
				<GalleryModal
					currentImage={orderedImages[expandedImageIndex]}
					headerSlot={
						<GalleryModalHeader
							currentImage={orderedImages[expandedImageIndex]}
							imagesCount={orderedImages.length}
							onClose={handleCloseModal}
						/>
					}
					onClose={handleCloseModal}
					thumbnails={{
						l: orderedImages[expandedImageIndex].url || '',
						m: '',
						s: '',
						xs: ''
					}}
					thumbnailsSlot={
						<GalleryModalThumbnails
							currentImageId={
								orderedImages[expandedImageIndex].id
							}
							images={orderedImages.map((image) => ({
								id: image.id,
								name: image.name,
								thumbnails: {
									l: image.url || '',
									m: image.url || '',
									s: image.url || '',
									xs: image.url || ''
								}
							}))}
							onSelect={(_, index) => {
								setExpandedImageIndex(index)
							}}
							thumbnails='thumbnails'
						/>
					}
				/>
			)}

			{imageToDelete !== null && (
				<ProPhotosDeleteImageModal
					isPending={deletePhotoMutation.isPending}
					onClose={onCloseDeletePhotoModal}
					onDelete={(e) => onClickDeletePhoto(e, imageToDelete.id)}
					store={deleteImageStore}
				/>
			)}
		</>
	)
}
